import { useCallback, useMemo } from 'react'

import type {
  CampaignBannerHeaderContentBlock,
  CompactCampaignBannerHeaderContentBlock,
} from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { useBasket } from 'routes/Menu/domains/basket'

type MerchandisingBannerBasketContentBlock =
  | CampaignBannerHeaderContentBlock
  | CompactCampaignBannerHeaderContentBlock

type MerchandisingBannerBasketItem =
  | CampaignBannerHeaderContentBlock['items'][number]
  | CompactCampaignBannerHeaderContentBlock['items'][number]

type CampaignBannerRecipeItem = Extract<
  CampaignBannerHeaderContentBlock['items'][number],
  { type: 'recipe' }
>

type CompactCampaignBannerRecipeItem = CompactCampaignBannerHeaderContentBlock['items'][number]

type MerchandisingBannerBasketRecipeItem =
  | CampaignBannerRecipeItem
  | CompactCampaignBannerRecipeItem

const isMerchandisingBannerRecipeItem = (
  item: MerchandisingBannerBasketItem,
): item is MerchandisingBannerBasketRecipeItem => item.type === 'recipe'

export const useMerchandisingBannerBasket = (
  contentBlock: MerchandisingBannerBasketContentBlock,
) => {
  const {
    addRecipeByUuid,
    canAddRecipes,
    getQuantitiesForRecipeId,
    reachedLimit,
    removeRecipeByUuid,
  } = useBasket()

  const recipeById = useMemo(() => {
    const items = contentBlock.items as MerchandisingBannerBasketItem[]

    return Object.fromEntries(
      items.filter(isMerchandisingBannerRecipeItem).map((recipe) => [recipe.id, recipe]),
    ) as Partial<Record<RecipeUUID, MerchandisingBannerBasketRecipeItem>>
  }, [contentBlock.items])

  const getRecipeCardState = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]
      const isInBasket = getQuantitiesForRecipeId(recipeId) > 0

      return {
        isInBasket,
        isOutOfStock: recipe ? !recipe.isAvailable && !isInBasket : false,
      }
    },
    [getQuantitiesForRecipeId, recipeById],
  )

  const onAddClick = useCallback(
    (recipeId: RecipeUUID) => {
      const recipe = recipeById[recipeId]

      if (!recipe?.isAvailable) {
        return
      }

      addRecipeByUuid(recipeId, 'view_recipe_list_screen', {
        position: 'collection_merchandising_header',
        source: 'merch_banner',
        merch_header_type: contentBlock.type,
      })
    },
    [addRecipeByUuid, contentBlock.type, recipeById],
  )

  const onRemoveClick = useCallback(
    (recipeId: RecipeUUID) => {
      removeRecipeByUuid(recipeId, 'grid', 'collection_merchandising_header')
    },
    [removeRecipeByUuid],
  )

  return {
    canAddRecipes,
    getRecipeCardState,
    onAddClick,
    onRemoveClick,
    reachedLimit,
  }
}
